import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { langDate, langMonth } from "../../../../../utils/properties";
import Filter from "../../../../../components/functional_components/Filter/Filter";
import { useFormik } from "formik";
import { get, toLower } from "lodash";
import { buildQueryString, formatNumber, showError } from "../../../../../utils/utilities";
import { useParams } from "react-router-dom";
import { setFilters } from "../../../../../redux/ducks/Filters";
import { Table } from "antd";
import ProjectInvoiceStatement from "../../../../../services/api/ProjectInvoiceStatement";
import { setLoading } from "../../../../../redux/ducks/Loading";
import ImportPlanning from "../../../../../services/api/ImportPlanning";
import dayjs from "dayjs";

const Progress = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentDate = dayjs().format("YYYYMM");
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const params = useParams();
  const [state, setState] = useState({
    tableContent: [],
    loading: false,
    filterLoading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    lastMonth: currentDate,
  });

  useEffect(() => {
    const queryString = buildQueryString({}, { yearMonth: currentDate });
    dispatch(setFilters(queryString));
    getData(params.id, queryString.string, true);
  }, []);

  const getLastMonth = async () => {
    const data = await ImportPlanning.lastFreeze();
    if (!data.error) {
      setState((prevState) => ({ ...prevState, lastMonth: data.data.month }));
      return `yearMonth=${data.data.month}`;
    } else {
      showError(get(data, "error.messageCode", "ER000"));
      return currentDate;
    }
  };

  const getData = async (id, queryString, lastMonth = false) => {
    dispatch(setLoading(true));
    let data = {};
    if (lastMonth) {
      queryString = await getLastMonth();
    }
    data = await ProjectInvoiceStatement.getDataProgress(id, queryString);
    if (!data.error) {
      data.data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({ ...prevState, tableContent: data.data }));
    } else {
      showError(get(data, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const formikFilter = useFormik({
    initialValues: {
      month: dayjs(state.lastMonth.toString() || currentDate, "YYYYMM"),
    },
    validate: (values) => {
      let errors = {};
      if (!values.month) errors.month = "Required!";
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const month = dayjs(values.month, "YYYYMM").format("YYYYMM");
      getData(params.id, `yearMonth=${month}`);
    },
  });

  const fieldsFilter = [
    {
      type: "month",
      isRequired: true,
      id: "month",
      name: "month",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("projectsManagement.incomeStatement.filter.month"),
      value: formikFilter.values.month,
      onChange: (date) => {
        formikFilter.setFieldValue("month", date);
      },
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString({}, { yearMonth: state.lastMonth !== null ? state.lastMonth : currentDate });
    formikFilter.setFieldValue("month", dayjs(queryString.obj.yearMonth, "YYYYMM"));
    dispatch(setFilters(queryString));
    getData(params.id, queryString.string);
  };

  const columns = [
    {
      title: "",
      dataIndex: "activity",
      width: 200,
      fixed: "left",
      render: (activity, record) => {
        let name = "";
        switch (record.type) {
          case "REVENUE":
            name = t("projectsManagement.incomeStatement.revenue");
            break;
          case "VALUE_INTERNAL":
            name = t("projectsManagement.incomeStatement.internal");
            break;
          case "VALUE_EXTERNAL":
            name = t("projectsManagement.incomeStatement.external");
            break;
          case "LABOR":
            name = t("projectsManagement.incomeStatement.labor");
            break;
          case "PERCENT_LABOR":
            name = t("projectsManagement.incomeStatement.percent");
            break;
          case "PERCENT_EXPENSE":
            name = t("projectsManagement.incomeStatement.percent");
            break;
          case "PERCENT_MARGIN":
            name = t("projectsManagement.incomeStatement.percent");
            break;
          case "EXPENSE":
            name = t("projectsManagement.incomeStatement.expense");
            break;
          case "COSTS":
            name = t("projectsManagement.incomeStatement.costs");
            break;
          case "MARGIN":
            name = t("projectsManagement.incomeStatement.margin");
            break;
          default:
            name = activity;
            break;
        }
        return name;
      },
    },
    {
      title: t("projectsManagement.incomeStatement.month"),
      children: [
        {
          title: t("projectsManagement.incomeStatement.m"),
          dataIndex: ["monthValues", "M"],
          width: 100,
          className: "table-value-right toSeeBetterShadow",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
        {
          title: t("projectsManagement.incomeStatement.m_1"),
          dataIndex: ["monthValues", "M_1"],
          width: 100,
          className: "table-value-right toSeeBetterShadow",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
        {
          title: t("projectsManagement.incomeStatement.budget"),
          dataIndex: ["monthValues", "BUDGET"],
          width: 100,
          className: "table-value-right toSeeBetterShadow",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
      ],
    },
    {
      title: t("projectsManagement.incomeStatement.yearToDate"),
      children: [
        {
          title: t("projectsManagement.incomeStatement.m"),
          dataIndex: ["yearToDateValues", "M"],
          width: 100,
          className: "table-value-right",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
        {
          title: t("projectsManagement.incomeStatement.m_1"),
          dataIndex: ["yearToDateValues", "M_1"],
          width: 100,
          className: "table-value-right",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
        {
          title: t("projectsManagement.incomeStatement.budget"),
          dataIndex: ["yearToDateValues", "BUDGET"],
          width: 100,
          className: "table-value-right",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
      ],
    },
    {
      title: t("projectsManagement.incomeStatement.lifeToDate"),
      children: [
        {
          title: t("projectsManagement.incomeStatement.m"),
          dataIndex: ["lifeToDateValues", "M"],
          width: 100,
          className: "table-value-right toSeeBetterShadow",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
        {
          title: t("projectsManagement.incomeStatement.m_1"),
          dataIndex: ["lifeToDateValues", "M_1"],
          width: 100,
          className: "table-value-right toSeeBetterShadow",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
        {
          title: t("projectsManagement.incomeStatement.budget"),
          dataIndex: ["lifeToDateValues", "BUDGET"],
          width: 100,
          className: "table-value-right toSeeBetterShadow",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
      ],
    },
    {
      title: t("projectsManagement.incomeStatement.totalContractValue"),
      children: [
        {
          title: t("projectsManagement.incomeStatement.m"),
          dataIndex: ["totalContractValues", "M"],
          width: 100,
          className: "table-value-right",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
        {
          title: t("projectsManagement.incomeStatement.m_1"),
          dataIndex: ["totalContractValues", "M_1"],
          width: 100,
          className: "table-value-right",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
        {
          title: t("projectsManagement.incomeStatement.budget"),
          dataIndex: ["totalContractValues", "BUDGET"],
          width: 100,
          className: "table-value-right",
          render: (amount, record) =>
            formatNumber({
              decimalScale:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? 1
                  : 0,
              value: amount,
              lang: currentLanguage,
              suffix:
                record.type === "PERCENT_LABOR" || record.type === "PERCENT_EXPENSE" || record.type === "PERCENT_MARGIN"
                  ? "PERCENT"
                  : "EUR",
            }),
        },
      ],
    },
  ];

  return (
    <>
      <Filter
        formikForm={formikFilter}
        fields={fieldsFilter}
        loading={state.filterLoading}
        allInOneRow={false}
        onReset={resetFilter}
      />
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={false}
        scroll={{ x: 1500 }}
        rowClassName={(record) => record.type}
        bordered
      />
    </>
  );
};

export default Progress;
